import { prettyEnglishDate } from "@app/date";
import {
  fetchLlmGatewayConfiguration,
  fetchLlmPolicies,
  formatCurrency,
  selectIsAccountOwner,
  selectLlmGatewayConfiguration,
  selectOrgAiUsageTotals,
} from "@app/deploy";
import { useCompositeLoader, useSelector } from "@app/react";
import { supportUrl } from "@app/routes";
import { dedent } from "@app/string-utils";
import { Box } from "@app/ui/shared/box";
import { IconInfo, IconLock, IconSettings } from "@app/ui/shared/icons";
import { LoadingSpinner } from "@app/ui/shared/loading";
import { ProgressBar } from "@app/ui/shared/progress-bar";
import type { ProgressBarVariant } from "@app/ui/shared/progress-bar";
import { Tooltip } from "@app/ui/shared/tooltip";
import { DateTime } from "luxon";
import { useState } from "react";
import { Link } from "react-router-dom";
import { tokens } from "../tokens";
import { EditAiGatewaySettingsModal } from "./edit-ai-gateway-settings-modal";

// --- Helpers ---

function spendVariantFor(spend: number, limit: number): ProgressBarVariant {
  if (limit === 0) return "default";
  const pct = (spend / limit) * 100;
  if (pct >= 100) return "error";
  if (pct >= 80) return "warning";
  return "default";
}

// The organization's LiteLLM team spend is the counter budget enforcement
// compares against the limit; the per-policy sum only covers environments
// that still exist, so it serves as a fallback.
function useOrgSpend() {
  const config = useSelector(selectLlmGatewayConfiguration);
  const { totalSpend: policySpendTotal } = useSelector(selectOrgAiUsageTotals);
  return {
    config,
    totalSpend: config?.currentUsage?.spend ?? policySpendTotal,
  };
}

// --- Sub-components ---

const TrialSpendWidget = ({ isLoading }: { isLoading: boolean }) => {
  const { config, totalSpend } = useOrgSpend();
  const spendLimit = config?.spendLimit ?? 0;

  return (
    <Box size="md">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">
            Trial Credit Spend
            <LoadingSpinner
              variant="xs"
              className="inline-block ml-2"
              show={isLoading}
            />
          </h3>
          <Tooltip
            fluid
            text="Spend limit cannot be edited while on a trial plan"
          >
            <IconLock variant="sm" className="opacity-60" />
          </Tooltip>
        </div>
        <div className="flex flex-col">
          <ProgressBar
            value={totalSpend}
            maxValue={spendLimit}
            variant={spendVariantFor(totalSpend, spendLimit)}
            className="mb-2"
            height="lg"
          />
          <div className="flex items-start justify-between text-sm text-gray-500">
            <div className={`${tokens.type.darker} text-base`}>
              {formatCurrency(totalSpend)} used
            </div>
            <div className="flex flex-col gap-1 items-end">
              <span className={`${tokens.type.darker} text-base`}>
                Limit: {formatCurrency(spendLimit)}
              </span>
              <div>
                Ready to sign up for more credits?{" "}
                <Link
                  className="underline"
                  to={supportUrl({
                    subject: "Upgrade to paid LLM Gateway plan",
                    message: dedent(
                      `
                      To upgrade to a paid LLM Gateway plan, submit this form and the Aptible Support team will be in touch soon.

                      The LLM Gateway is $400/month plus usage fees. Learn more about pricing here: https://www.aptible.com/pricing
                    `.trim(),
                    ),
                  })}
                >
                  Contact us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};

interface SpendWidgetProps {
  isLoading: boolean;
  orgId: string;
  onEditClick: () => void;
}

const SpendWidget = ({ isLoading, orgId, onEditClick }: SpendWidgetProps) => {
  const { config, totalSpend } = useOrgSpend();
  const isAccountOwner = useSelector((s) => selectIsAccountOwner(s, { orgId }));
  const spendLimit = config?.spendLimit ?? 0;
  // Paid budgets reset on the gateway's schedule, not the calendar month.
  const resetDate =
    config?.currentUsage?.budgetResetAt ??
    DateTime.now().plus({ months: 1 }).startOf("month").toISO();

  return (
    <Box size="md">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">
            Monthly Spend (MTD)
            <LoadingSpinner
              variant="xs"
              className="inline-block ml-2"
              show={isLoading}
            />
          </h3>
          {isAccountOwner ? (
            <Tooltip fluid text="Edit the monthly spend limit">
              <div
                className="cursor-pointer text-gray-500 hover:text-gray-900"
                aria-label="Edit the monthly spend limit"
                onClick={onEditClick}
                onKeyUp={(e) => {
                  if (["Enter", " "].includes(e.key)) onEditClick();
                }}
              >
                <IconSettings
                  variant="sm"
                  color="currentColor"
                  className="w-4 h-4"
                />
              </div>
            </Tooltip>
          ) : (
            <Tooltip
              fluid
              text="Only account owners can edit the monthly spend limit"
            >
              <IconLock variant="sm" className="ml-1 opacity-60" />
            </Tooltip>
          )}
        </div>
        <div className="flex flex-col">
          <ProgressBar
            value={totalSpend}
            maxValue={spendLimit}
            variant={spendVariantFor(totalSpend, spendLimit)}
            className="mb-2"
            height="lg"
          />
          <div className="flex items-start justify-between text-sm text-gray-500">
            <div className={`${tokens.type.darker} text-base`}>
              {formatCurrency(totalSpend)} used
            </div>
            <div className="flex flex-col gap-1 items-end">
              <span className={`${tokens.type.darker} text-base`}>
                Limit: {formatCurrency(spendLimit)}
              </span>
              <div>Refreshes {prettyEnglishDate(resetDate)}</div>
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};

const TokenUsageWidget = ({ isLoading }: { isLoading: boolean }) => {
  const { totalTokens, totalInputTokens, totalOutputTokens } = useSelector(
    selectOrgAiUsageTotals,
  );

  return (
    <Box size="md">
      <div className="flex flex-col gap-4">
        <h4 className="text-base font-semibold text-gray-900 flex space-x-1 items-center w-fit">
          <span>Token Usage</span>
          <Tooltip text="This is the total number of tokens used across all keys for the current month.">
            {isLoading ? (
              <LoadingSpinner variant="xs" className="inline-block ml-2" />
            ) : (
              <IconInfo
                className="inline-block opacity-50 hover:opacity-100"
                variant="sm"
              />
            )}
          </Tooltip>
        </h4>
        <div className="text-2xl">{totalTokens.toLocaleString()}</div>
        <div className="text-sm text-gray-500">
          {totalInputTokens.toLocaleString()} input /{" "}
          {totalOutputTokens.toLocaleString()} output
        </div>
      </div>
    </Box>
  );
};

const TotalRequestsWidget = ({ isLoading }: { isLoading: boolean }) => {
  const { totalRequests, totalSuccessfulRequests, totalFailedRequests } =
    useSelector(selectOrgAiUsageTotals);

  return (
    <Box size="md">
      <div className="flex flex-col gap-4">
        <h4 className="text-base font-semibold text-gray-900 flex space-x-1 items-center w-fit">
          <span>Total Requests</span>
          <Tooltip text="This is the total number of requests made across all keys for the current month.">
            {isLoading ? (
              <LoadingSpinner variant="xs" className="inline-block ml-2" />
            ) : (
              <IconInfo
                className="inline-block opacity-50 hover:opacity-100"
                variant="sm"
              />
            )}
          </Tooltip>
        </h4>
        <div className="text-2xl">{totalRequests.toLocaleString()}</div>
        <div className="text-sm text-gray-500">
          {totalSuccessfulRequests.toLocaleString()} successful /{" "}
          {totalFailedRequests.toLocaleString()} failed
        </div>
      </div>
    </Box>
  );
};

// --- Orchestrator ---

interface OrgAiUsageSummaryProps {
  orgId: string;
}

export const OrgAiUsageSummary = ({ orgId }: OrgAiUsageSummaryProps) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { isLoading } = useCompositeLoader([
    fetchLlmGatewayConfiguration,
    fetchLlmPolicies,
  ]);

  const config = useSelector(selectLlmGatewayConfiguration);
  const isTrial = config?.trial ?? false;

  return (
    <>
      <div className="grid grid-cols-3 gap-4">
        {isTrial ? (
          <TrialSpendWidget isLoading={isLoading} />
        ) : (
          <SpendWidget
            isLoading={isLoading}
            orgId={orgId}
            onEditClick={() => setIsEditModalOpen(true)}
          />
        )}
        <TokenUsageWidget isLoading={isLoading} />
        <TotalRequestsWidget isLoading={isLoading} />
      </div>

      {config && (
        <EditAiGatewaySettingsModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          config={config}
        />
      )}
    </>
  );
};
